import type { ButtonDropdownProps } from '@amzn/awsui-components-core/button-dropdown';
import { PROFILE_DROPDOWN_ITEMS } from 'src/components/dfp-home-page/top-navigation/profile-dropdown-constants';
import { RecentEntityType } from '../RecentsConstants';
import type { RecentsHydrator } from './types';

/** Hydrated profile item — enough to render a link + navigate. */
export interface ResolvedProfileItem {
  readonly id: string;
  readonly text: string;
  readonly href: string;
  /** Parent group id (e.g. `general`, `workplace`) — useful for display grouping. */
  readonly groupId: string;
}

/**
 * Flatten `PROFILE_DROPDOWN_ITEMS` into a lookup map of `id -> item`, built
 * once at module-load. Profile items are a static import, so the map is
 * computed exactly once per bundle load — zero per-call overhead.
 *
 * If an id ever gets renamed in `PROFILE_DROPDOWN_ITEMS`, the stored recents
 * entry with the old id will simply fail to resolve (returns `null` below)
 * and the hook will drop it — exactly the same graceful behavior as a
 * deactivated app.
 */
const PROFILE_ITEM_INDEX: ReadonlyMap<string, ResolvedProfileItem> = (() => {
  const map = new Map<string, ResolvedProfileItem>();
  for (const group of PROFILE_DROPDOWN_ITEMS) {
    const itemGroup = group as ButtonDropdownProps.ItemGroup;
    const groupId = itemGroup.id ?? '';
    for (const subItem of itemGroup.items ?? []) {
      const item = subItem as ButtonDropdownProps.Item;
      if (!item.id || !item.href) continue;
      map.set(item.id, {
        id: item.id,
        text: typeof item.text === 'string' ? item.text : String(item.text ?? ''),
        href: item.href,
        groupId
      });
    }
  }
  return map;
})();

/**
 * Synchronous profile-item hydrator. Pure function — no React / context
 * dependency, so it's safe to call from anywhere (component, service, test).
 */
export const profileHydrator: RecentsHydrator<ResolvedProfileItem> = {
  type: RecentEntityType.PROFILE,
  resolve(entityId) {
    return PROFILE_ITEM_INDEX.get(entityId) ?? null;
  }
};
